import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Bell,
  Calendar,
  HandCoins,
  CreditCard,
  CheckCircle2,
  Info,
  Loader2,
  CheckCheck,
  ChevronRight,
  Inbox,
} from "lucide-react";
import { format, isToday, isYesterday, isThisWeek, isThisMonth, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Link, useNavigate } from "react-router-dom";

import { AlertTriangle, Ban } from "lucide-react";

const NOTIFICATION_ICONS: Record<string, React.ElementType> = {
  meeting_reminder: Calendar,
  meeting_summary: Info,
  contribution_reminder: HandCoins,
  contribution_verified: CheckCircle2,
  loan_update: CreditCard,
  loan_created: CreditCard,
  loan_approved: CheckCircle2,
  loan_rejected: CreditCard,
  loan_applied: CreditCard,
  contribution_declared: HandCoins,
  contribution_approved: CheckCircle2,
  contribution_deducted: HandCoins,
  penalty_applied: AlertTriangle,
  penalty_paid: CheckCircle2,
  penalty_waived: Ban,
};

function NotificationSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100">
          <div className="h-9 w-9 rounded-full bg-slate-200 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-slate-200 rounded w-3/4" />
            <div className="h-3 bg-slate-100 rounded w-1/2" />
          </div>
          <div className="h-3 w-12 bg-slate-100 rounded shrink-0" />
        </div>
      ))}
    </div>
  );
}

function groupNotifications(notifications: any[]) {
  const groups: { label: string; items: any[] }[] = [];
  const today: any[] = [];
  const yesterday: any[] = [];
  const thisWeek: any[] = [];
  const thisMonth: any[] = [];
  const older: any[] = [];

  notifications.forEach((n) => {
    const d = new Date(n.createdAt);
    if (isToday(d)) today.push(n);
    else if (isYesterday(d)) yesterday.push(n);
    else if (isThisWeek(d)) thisWeek.push(n);
    else if (isThisMonth(d)) thisMonth.push(n);
    else older.push(n);
  });

  if (today.length) groups.push({ label: "Today", items: today });
  if (yesterday.length) groups.push({ label: "Yesterday", items: yesterday });
  if (thisWeek.length) groups.push({ label: "This Week", items: thisWeek });
  if (thisMonth.length) groups.push({ label: "This Month", items: thisMonth });
  if (older.length) groups.push({ label: "Older", items: older });
  return groups;
}

export default function MemberNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"all" | "unread">("all");

  const { data: notificationsData, isLoading } = useQuery(
    ["my-notifications", user?.id],
    async () => {
      const { data } = await api.get("/notifications/my");
      return data.data || [];
    },
    { enabled: !!user?.id }
  );

  const { data: unreadCountData } = useQuery(
    ["notif-unread-count"],
    async () => {
      const { data } = await api.get("/notifications/my/unread-count");
      return data.data;
    },
    { enabled: !!user?.id }
  );

  const markReadMutation = useMutation(
    async (ids: string[] | null) => {
      if (ids && ids.length === 1) {
        const { data } = await api.patch(`/notifications/my/${ids[0]}/read`);
        return data;
      }
      const { data } = await api.patch("/notifications/my/read", ids ? { notificationIds: ids } : {});
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["my-notifications"]);
        queryClient.invalidateQueries(["notif-unread-count"]);
        toast.success("Notifications marked as read");
      },
      onError: () => {
        toast.error("Failed to mark notifications as read");
      },
    }
  );

  const notifications = notificationsData || [];
  const unreadCount = unreadCountData?.unreadCount ?? 0;
  const filteredNotifications = filter === "unread" ? notifications.filter((n: any) => !n.isRead) : notifications;
  const grouped = useMemo(() => groupNotifications(filteredNotifications), [filteredNotifications]);
  const totalCount = notifications.length;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-slate-500">
        <Link to="/member/dashboard" className="hover:text-[#009A44] transition-colors">Dashboard</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-700 font-medium">Notifications</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Notifications</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {unreadCount > 0
              ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""}`
              : totalCount > 0
              ? "All caught up!"
              : "No notifications yet"}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => markReadMutation.mutate(null)}
            disabled={markReadMutation.isLoading}
            className="gap-2"
          >
            {markReadMutation.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCheck className="h-4 w-4" />
            )}
            Mark All Read
          </Button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setFilter("all")}
          className={cn(
            "px-4 py-1.5 text-sm font-medium rounded-lg transition-colors",
            filter === "all"
              ? "bg-[#009A44] text-white shadow-sm"
              : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
          )}
        >
          All
          {totalCount > 0 && (
            <span className={cn("ml-1.5 text-xs", filter === "all" ? "text-white/80" : "text-slate-400")}>
              ({totalCount})
            </span>
          )}
        </button>
        <button
          onClick={() => setFilter("unread")}
          className={cn(
            "px-4 py-1.5 text-sm font-medium rounded-lg transition-colors",
            filter === "unread"
              ? "bg-[#009A44] text-white shadow-sm"
              : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
          )}
        >
          Unread
          {unreadCount > 0 && (
            <span className={cn("ml-1.5 text-xs", filter === "unread" ? "text-white/80" : "text-slate-400")}>
              ({unreadCount})
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <NotificationSkeleton />
      ) : filteredNotifications.length === 0 ? (
        <Card className="border border-slate-200 shadow-sm rounded-xl">
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-400">
            <div className="h-16 w-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
              <Inbox className="h-8 w-8 text-slate-300" />
            </div>
            <p className="text-sm font-medium text-slate-500">No notifications</p>
            <p className="text-xs text-slate-400 mt-1">
              {filter === "unread" ? "All caught up! No unread notifications." : "You haven't received any notifications yet."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.label}>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                {group.label}
              </h3>
              <div className="space-y-2">
                {group.items.map((notification: any) => {
                  const IconComponent = NOTIFICATION_ICONS[notification.type] || Bell;
                  return (
                    <div
                      key={notification.id}
                      className={cn(
                        "flex items-start gap-3 p-4 bg-white rounded-xl border transition-all cursor-pointer",
                        !notification.isRead
                          ? "border-l-[3px] border-l-[#009A44] border-slate-200 shadow-sm hover:shadow-md"
                          : "border-slate-100 hover:border-slate-200 hover:shadow-sm"
                      )}
                      onClick={() => {
                        if (!notification.read) {
                          markReadMutation.mutate([notification.id] as string[]);
                        }
                        const url = notification.actionUrl;
                        if (url) {
                          navigate(url);
                        } else if (notification.relatedEntityType === "Loan") {
                          navigate("/member/loans");
                        } else if (notification.relatedEntityType === "MonthlySavingDeclaration" || notification.relatedEntityType === "MemberPayment") {
                          navigate("/member/contributions");
                        } else if (notification.relatedEntityType === "SupportTicket") {
                          navigate("/member/support");
                        } else if (notification.relatedEntityType === "PenaltyRecord") {
                          navigate("/member/penalties");
                        }
                      }}
                    >
                        <div
                          className={cn(
                            "flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center",
                            !notification.isRead ? "bg-[#009A44]/10" : "bg-slate-100"
                          )}
                        >
                          <IconComponent
                            className={cn(
                              "h-4 w-4",
                              !notification.isRead ? "text-[#009A44]" : "text-slate-400"
                            )}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p
                              className={cn(
                                "text-sm",
                                !notification.isRead ? "font-semibold text-slate-800" : "text-slate-600"
                              )}
                            >
                              {notification.title}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                          </div>
                          <div className="flex-shrink-0 flex flex-col items-end gap-1.5 ml-2">
                            {!notification.isRead && (
                              <span className="w-2 h-2 rounded-full bg-[#009A44]" />
                            )}
                            <span className="text-[10px] text-slate-400 whitespace-nowrap">
                              {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
